import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import RegistrationContext from "../contexts/RegistrationContext";
import { base_url } from "../config";
import WhiteBoxLayout from "../components/WhiteBoxLayout/WhiteBoxLayout";
import { Check, Plus, X, Search, Info } from "lucide-react";



function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">{children}</div>
      </div>
    </div>
  );
}

function PaperSelection() {
  const { setError, reload, setReload, data, papers: data_papers } =
    useContext(RegistrationContext);

  // State to keep track of selected optons
  const [selectedPapers, setSelectedPapers] = useState<string[]>(data.paper_selection);
  const [isPaperModalOpen, setIsPaperModalOpen] = useState(false);
  const [searchPaper, setSearchPaper] = useState("");

  const filteredPapers = data_papers.filter(
    (paper:any) =>
      paper.title.toLowerCase().includes(searchPaper.toLowerCase()) ||
      paper.paper_id.toString()
        .toLowerCase()
        .includes(searchPaper.toLowerCase()) ||
      paper.author_names.toLowerCase().includes(searchPaper.toLowerCase())
  );

  const handleSelect = async () => {
    // Reset login error state

    try {
      // Make a POST request to your authentication endpoint

      await axios.post(`${base_url}/api/progress`, {});
      setReload(reload + 1);
    } catch (error: any) {
      setError("Unexpected error. " + error.response.data.message);
    }
  };
  const hasMounted = useRef(false);
  useEffect(() => {
    const updateData = async () => {
      try {
        await axios.post(`${base_url}/api/selected_papers`, {
          selected_papers: selectedPapers,
        });
      } catch (error: any) {
        setError("Unexpected error. " + error.response.data.message);
      }
    };

    if (hasMounted.current) {
      updateData();
    } else {
      hasMounted.current = true;
    }
  }, [selectedPapers, setError]);

  const toglePaper = (id: string) => {
    if (selectedPapers.includes(id)) {
      // If the paper is already selected, allow deselection
      setSelectedPapers((prev) => prev.filter((wId) => wId !== id));
    } else {
      if (selectedPapers.length >= 6) {
        // Display an error if the user tries to select more than 5 papers
        setError("You can select a maximum of 6 papers.");
        return;
      }
      // Add the paper to the selection
      setSelectedPapers((prev) => [...prev, id]);
    }
  };

  return (
    <>
      <WhiteBoxLayout title={"Personal data"} handleSelect={() => handleSelect()}>
          <h1 className="text-xl font-bold mb-4">Paper Selection</h1>

          <div className=" rounded-md p-3 mb-4 flex items-start border-sky-200 border">
            <Info className="h-5 w-5 text-sky-500 mr-2 flex-shrink-0 mt-0.5" />

            <div className=" rounded-md text-sm text-sky-800 ">
              Kindly select the papers for which you are an author.
              <ul className="mt-2">
                {" "}
                <li className="list-disc ml-5">
                  {" "}
                  You may register an unlimited number of papers at no
                  additional cost, provided you are listed as an author.{" "}
                </li>{" "}
                <li className="list-disc ml-5">
                  {" "}
                  Please ensure that each paper has at least one registration
                  covering it.{" "}
                </li>{" "}
                <li className="list-disc ml-5">
                  {" "}
                  Workshop / Doctoral Consortium papers do not require
                  registration.{" "}
                </li>{" "}
              </ul>
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-base font-semibold">Selected Papers</h2>
              <button
                onClick={() => setIsPaperModalOpen(true)}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </button>
            </div>

            {selectedPapers.length === 0 ? (
              <div className="p-2 border border-dashed rounded-md text-center text-gray-500 text-sm">
                No papers selected
              </div>
            ) : (
              <div className="space-y-2  overflow-y-auto pr-1">
                {selectedPapers.map((paper_id) => {
                  const paper = data_papers.find((w:any) => w.paper_id === paper_id);
                  if (!paper) return null;

                  return (
                    <div
                      key={paper_id}
                      className="p-2 rounded-md border border-blue-600 bg-blue-50 text-sm"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1 mr-2">
                          <h3 className="font-medium">
                            <b>{paper.paper_id}</b>: {paper.title}
                          </h3>
                          <div className="mt-1">
                            <span className="text-xs text-blue-700">
                              {paper.author_names}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPapers((prev) =>
                              prev.filter((wId) => wId !== paper_id)
                            );
                          }}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Modal
            isOpen={isPaperModalOpen}
            onClose={() => setIsPaperModalOpen(false)}
            title="Available Papers"
          >
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Search paper..."
                className="w-full pl-8 pr-2 py-1.5 border rounded-md text-sm"
                value={searchPaper}
                onChange={(e) => setSearchPaper(e.target.value)}
              />
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredPapers.map((paper:any) => (
                <div
                  key={paper.paper_id}
                  onClick={() => toglePaper(paper.paper_id)}
                  className={`p-2 rounded-md border cursor-pointer transition-colors text-sm ${
                    selectedPapers.includes(paper.paper_id)
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1 mr-2">
                      <h3 className="font-medium">
                        <b>{paper.paper_id}</b>: {paper.title}
                      </h3>

                      <div className="mt-1 text-xs text-blue-700">
                        {paper.author_names}
                      </div>
                    </div>
                    <div
                      className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                        selectedPapers.includes(paper.paper_id)
                          ? "bg-blue-600 border-blue-600"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedPapers.includes(paper.paper_id) && (
                        <Check className="h-2 w-2 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredPapers.length === 0 && (
                <div className="p-2 text-center text-gray-500 text-sm">
                  No papers found matching your search.
                </div>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => setIsPaperModalOpen(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded text-sm"
              >
                Done
              </button>
            </div>
          </Modal>
      </WhiteBoxLayout>
    </>
  );
}

export default PaperSelection;
